/**
 * As ABAS de cada módulo, para o `secao` do `navegar` (14/09/2026).
 *
 * "Abra a tela de leads" abria `/crm` (Bem-vindo): o enum `destino` do backend
 * só conhece o MÓDULO. O parâmetro opcional `secao` (texto livre — "Leads",
 * "Relacionamento", "Transcrições") é resolvido AQUI contra as abas reais do
 * módulo de destino, e o resultado é uma rota-filha que existe e que o
 * escritório pode abrir — nunca um nome inventado pelo modelo.
 *
 * ★ A fonte de cada módulo é a MESMA função que o seu `*-nav-bar.vue` consome
 * (`crmRoutes`, `taskRoutes`, `consolidadorOptionsRoutes`…) — o padrão do
 * `SUBITEM_RESOLVERS` de `navegacao-v2/utils/cliente-subitems.js`. Não é o
 * `router.getRoutes()` + `meta.search.title`: aquele título é o da BUSCA
 * ("Funil de leads"), não o rótulo da aba ("Leads"), e só parte das rotas o
 * declara. Ler a lista da nav-bar é o que faz "Leads" no chat ser a mesma
 * "Leads" que o usuário vê na barra.
 *
 * As funções que já filtram por flag/perfil (`hasAccess`) chegam filtradas;
 * por cima de TODAS, `secoesDoModulo` ainda passa cada aba por
 * `rotaExisteNoRouter` + `podeAcessar` — o mesmo guard do destino de módulo
 * (QA-G-1): aba escondida por flag não vira candidata, e aba que cairia em
 * `/404` não é empurrada. ⚠️ Gate que vive SÓ na nav-bar (ex.: "Configurações"
 * do fee-fixo, gestor-only pelo `ehGestor` do shell) não é replicado aqui —
 * a rota dele tem `customPermission`, e o guard de rota da plataforma decide.
 *
 * Módulo fora do registro (BI, Clientes, Data Room…) devolve `[]`: o `navegar`
 * cai na entrada do módulo, como sempre, e avisa o modelo.
 */
import { normalizeText } from '@utils/Format'
import { routeModuleKey } from '@modules/navegacao-v2/utils/visible-modules'
import { crmRoutes } from '@modules/crm/constants/Text'
import {
  comunicacoesSubNavOptions,
  comunicacoesSubNavRouteName,
} from '@modules/crm/utils/comunicacoes-channels'
import { taskRoutes } from '@modules/tarefas/constants/Text'
import { consolidadorOptionsRoutes } from '@modules/consolidator/constants/Text'
import { complianceRoutes } from '@modules/compliance/constants/Text'
import { fpRoutes } from '@modules/financial-planning/constants/Text'
import { financeOptionsRoutes } from '@modules/finance/constants/Texts'
import { comissoesOptionsRoutes } from '@modules/comissoes/constants/Texts'
import { treasuryOptionsRoutes } from '@modules/tesouraria/constants/Text'
import { strategyOtionsRoutes } from '@modules/estrategia/constants/Texts'
import { captableRoutes } from '@modules/captable/constants/Text'
import { rhOptionsRoutes } from '@modules/rh/constants/Text'
import { wsRoutes } from '@modules/ws/constants/Text'
import { aawzDriveRoutes } from '@modules/aawz-drive/constants/Text'
import { aawzIntelligenceRoutes } from '@modules/aawz-intelligence-v2/constants/Text'
import { feeFixoRoutes } from '@modules/fee-fixo/constants/Text'
import { rotaExisteNoRouter, podeAcessar } from './catalogo'

/** A aba do CRM que agrupa os canais de comunicação — os canais entram logo após ela. */
const ABA_OMNICHANNEL = 'crm.comunicacoes'

/** `feature` de aba como a nav-bar do CRM lê: string OU array (AND). */
function temAFeature(feature, access) {
  if (!feature) return true
  if (Array.isArray(feature)) return feature.every((f) => Boolean(access[f]))
  return Boolean(access[feature])
}

/**
 * CRM: `crmRoutes()` com o MESMO filtro de `feature` da `crm-nav-bar.vue`
 * (Proposta R2 e Grupos Familiares somem sem a flag), e os canais do
 * Omnichannel (Mensagens, Email, Transcrições, Ligações — `comunicacoes-channels.js`,
 * a fonte da sub-nav de `/crm/comunicacoes`) logo depois da aba que os agrupa:
 * "Transcrições" é aba de verdade para o usuário, ainda que de segundo nível.
 */
function secoesDoCrm({ access }) {
  const podeVer = (feature) => temAFeature(feature, access)
  const canais = comunicacoesSubNavOptions(podeVer).map((canal) => ({
    name: comunicacoesSubNavRouteName(canal.value),
    title: canal.text,
  }))
  const abas = []
  crmRoutes()
    .filter((aba) => podeVer(aba.feature))
    .forEach((aba) => {
      abas.push(aba)
      if (aba.name === ABA_OMNICHANNEL) canais.forEach((canal) => abas.push(canal))
    })
  return abas
}

/**
 * `chave de módulo` (1º segmento do `name` da rota) → abas `{name, title}`
 * na ORDEM da nav-bar. A assinatura de cada função é a que o módulo declarou;
 * o adaptador chama cada uma na ordem certa.
 */
const FONTES_DE_SECOES = {
  crm: secoesDoCrm,
  task: ({ currentUser }) => taskRoutes(currentUser),
  carteira: ({ access, currentUser }) => consolidadorOptionsRoutes(currentUser, access),
  compliance: ({ access, currentUser }) => complianceRoutes(access, currentUser),
  fp: () => fpRoutes(),
  finance: ({ access, currentUser }) => financeOptionsRoutes(access, currentUser),
  comissoes: ({ access, currentUser }) => comissoesOptionsRoutes(currentUser, access),
  treasury: ({ access, currentUser }) => treasuryOptionsRoutes(currentUser, access),
  valuation: ({ access, currentUser }) => strategyOtionsRoutes(currentUser, access),
  captable: ({ access, currentUser }) => captableRoutes(access, currentUser),
  rh: ({ access, currentUser }) => rhOptionsRoutes(currentUser, access),
  ws: () => wsRoutes(),
  'aawz-drive': ({ access }) => aawzDriveRoutes(access),
  'aawz-intelligence': () => aawzIntelligenceRoutes(),
  'fee-fixo': () => feeFixoRoutes(),
}

/** Os módulos que têm abas conhecidas — para a mensagem ao modelo e para teste. */
export const MODULOS_COM_SECOES = Object.keys(FONTES_DE_SECOES)

/** `{access, currentUser}` lidos do contexto de execução das tools de front. */
function acessoDoContexto(ctx) {
  const store = ctx && ctx.store
  const vm = ctx && ctx.vm
  const getters = (store && store.getters) || {}
  const currentUser =
    vm && typeof vm.$currentUser === 'function' ? vm.$currentUser() : null
  return {
    access: getters['parameters/show'] || {},
    currentUser: currentUser || {},
  }
}

function lerFonte(fonte, acesso) {
  try {
    return fonte(acesso) || []
  } catch {
    // Fonte que lança com `currentUser` vazio (ex.: lê `.IS_GESTOR` de null)
    // não pode derrubar o turno: o módulo fica sem abas, e o navegar cai na
    // entrada como sempre.
    return []
  }
}

/**
 * As abas do módulo da rota `nomeDaRota`, já filtradas pelo guard.
 *
 * @param {string} nomeDaRota  `name` de QUALQUER rota do módulo (a entrada
 *   resolvida por `rotaDoDestino`, ou a rota atual da tela).
 * @param {Object} ctx  `{router, store, vm, canAccessRoute}`.
 * @returns {Array<{rotulo: string, name: string}>} na ordem da nav-bar.
 */
export function secoesDoModulo(nomeDaRota, ctx) {
  const fonte = FONTES_DE_SECOES[routeModuleKey(nomeDaRota)]
  if (!fonte) return []
  return lerFonte(fonte, acessoDoContexto(ctx))
    .filter((aba) => aba && aba.name && aba.title)
    .filter((aba) => rotaExisteNoRouter(ctx, aba.name) && podeAcessar(ctx, aba.name))
    .map((aba) => ({ rotulo: String(aba.title).trim(), name: aba.name }))
}

/** Só os rótulos — o que vai ao modelo em `secoes_disponiveis`/`secoes_do_modulo`. */
export function rotulosDasSecoes(secoes) {
  return (secoes || []).map((secao) => secao.rotulo)
}

/**
 * Os três níveis de casamento, do mais ao menos exato. Dentro de cada nível
 * vence a PRIMEIRA aba na ordem da nav-bar — é a régua do empate.
 */
const CASAMENTOS = [
  (rotulo, pedido) => rotulo === pedido,
  (rotulo, pedido) => rotulo.startsWith(pedido),
  (rotulo, pedido) => rotulo.includes(pedido),
]

/**
 * A aba que `secao` pede, ou `null`.
 *
 * Casamento por texto NORMALIZADO (`normalizeText`: sem acento, sem caixa,
 * sem espaço nas pontas) — "transcricoes", "Transcrições" e "TRANSCRIÇÕES"
 * são a mesma aba. Igualdade vence prefixo, que vence "contém"; empate no
 * mesmo nível → a primeira na ordem da nav-bar.
 *
 * @param {*} secao  `input.secao` cru, como veio do modelo.
 * @param {Array<{rotulo, name}>} secoes  saída de `secoesDoModulo`.
 * @returns {?{rotulo: string, name: string}}
 */
export function resolverSecao(secao, secoes) {
  if (typeof secao !== 'string') return null
  const pedido = normalizeText(secao)
  if (!pedido || !secoes || !secoes.length) return null
  const candidatas = secoes.map((aba) => ({
    aba: aba,
    rotulo: normalizeText(aba.rotulo),
  }))
  for (const casa of CASAMENTOS) {
    const achada = candidatas.find((c) => casa(c.rotulo, pedido))
    if (achada) return achada.aba
  }
  return null
}
